/**
 * WebAuthn/Passkeys Utility Functions
 * Handles biometric authentication (fingerprint, Face ID, Windows Hello)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Check if WebAuthn is supported in the current browser
 */
export const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined && 
         navigator.credentials !== undefined;
};

/**
 * Check if platform authenticator (biometrics) is available
 */
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnSupported()) return false;
  
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('Error checking platform authenticator:', error);
    return false;
  }
};

/**
 * Convert base64url string to ArrayBuffer
 */
const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Convert ArrayBuffer to base64url string
 */
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

/**
 * Register a new passkey for the user
 */
export const registerPasskey = async (userId, email, displayName) => {
  try {
    // Step 1: Get registration options from server
    const optionsResponse = await fetch(`${API}/webauthn/register/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId, email, display_name: displayName })
    });
    
    const responseData = await optionsResponse.json();
    
    if (!optionsResponse.ok) {
      throw new Error(responseData.detail || 'Failed to start passkey registration');
    }
    
    const options = responseData;
    
    // Step 2: Convert options for WebAuthn API
    const publicKeyOptions = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      user: {
        ...options.user,
        id: base64urlToBuffer(options.user.id)
      },
      excludeCredentials: options.excludeCredentials?.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }))
    };
    
    // Step 3: Create credential using browser's WebAuthn API
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions
    });
    
    if (!credential) {
      throw new Error('Failed to create credential');
    }
    
    // Step 4: Prepare credential for server
    const credentialForServer = {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
      }
    };
    
    // Step 5: Send credential to server for verification
    const verifyResponse = await fetch(`${API}/webauthn/register/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: userId,
        credential: credentialForServer
      })
    });
    
    const verifyData = await verifyResponse.json();
    
    if (!verifyResponse.ok) {
      throw new Error(verifyData.detail || 'Failed to verify passkey');
    }
    
    return verifyData;
    
  } catch (error) {
    console.error('Passkey registration error:', error);
    throw error;
  }
};

/**
 * Authenticate using a passkey
 */
export const authenticateWithPasskey = async (email) => {
  try {
    // Step 1: Get authentication options from server
    const optionsResponse = await fetch(`${API}/webauthn/authenticate/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    });
    
    const responseData = await optionsResponse.json();
    
    if (!optionsResponse.ok) {
      throw new Error(responseData.detail || 'Failed to start authentication');
    }
    
    const options = responseData;
    
    // Step 2: Convert options for WebAuthn API
    const publicKeyOptions = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: options.allowCredentials?.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }))
    };
    
    // Step 3: Get credential using browser's WebAuthn API
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyOptions
    });
    
    if (!assertion) {
      throw new Error('Authentication cancelled');
    }
    
    // Step 4: Prepare assertion for server
    const assertionForServer = {
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
        signature: bufferToBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle ? 
          bufferToBase64url(assertion.response.userHandle) : null
      }
    };
    
    // Step 5: Send assertion to server for verification
    const verifyResponse = await fetch(`${API}/webauthn/authenticate/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        credential: assertionForServer
      })
    });
    
    const verifyData = await verifyResponse.json();
    
    if (!verifyResponse.ok) {
      throw new Error(verifyData.detail || 'Authentication failed');
    }
    
    return verifyData;
    
  } catch (error) {
    console.error('Passkey authentication error:', error);
    throw error;
  }
};

/**
 * Get user's registered passkeys
 */
export const getUserPasskeys = async () => {
  try {
    const response = await fetch(`${API}/webauthn/credentials`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch passkeys');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching passkeys:', error);
    throw error;
  }
};

/**
 * Delete a passkey
 */
export const deletePasskey = async (credentialId) => {
  try {
    const response = await fetch(`${API}/webauthn/credentials/${credentialId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete passkey');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting passkey:', error);
    throw error;
  }
};
